import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';

import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';

import { ToastrService } from 'ngx-toastr';

import * as ccStudyParticipantActions from './cc-study-participant.actions';
import { DashboardCamelCasePipe } from '../../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from '../../../../core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { AppState } from '../../../../state';
import { CustomRouterNavigatedAction } from '../../../../core/custom-route-serializer';
import { STUDIES_ROUTES } from '../../../studies/studies.routes';
import { StudyParticipantsService } from '../../../services/study-participants/study-participants.service';

@Injectable()
export class CcStudyParticipantEffects {
  ccStudyParticipantsRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter((route: CustomRouterNavigatedAction) =>
        route.payload.routerState.path.endsWith(STUDIES_ROUTES.CC_PARTICIPANTS)
      ),
      map((action) =>
        ccStudyParticipantActions.getCcStudyParticipants({
          studyId: action.payload.routerState.params.id,
        })
      )
    )
  );

  getCcStudyParticipants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ccStudyParticipantActions.getCcStudyParticipants),
      switchMap(({ studyId }) => {
        return this.studyParticipantsService.getCcStudyParticipants(studyId).pipe(
          map((ccStudyParticipants) => {
            const reducedParticipants = ccStudyParticipants.reduce(
              (
                ccStudyParticipant,
                {
                  id,
                  email,
                  interventionID,
                  interventionName,
                  interventionHash,
                  userHash,
                  finishedDate,
                  notifiedAt,
                  updatedAt,
                },
                index
              ) => {
                ccStudyParticipant[id] = ccStudyParticipant[id] || {
                  id,
                  email,
                  finishedDate,
                  notifiedAt,
                  updatedAt,
                  interventions: [],
                };
                ccStudyParticipant[id].interventions.push({
                  interventionID,
                  interventionName,
                  interventionHash,
                  userHash,
                });
                return ccStudyParticipant;
              },
              {}
            );
            return ccStudyParticipantActions.getCcStudyParticipantsSuccess({
              ccStudyParticipants: Object.values(reducedParticipants),
            });
          }),
          catchError((error: HttpErrorResponse) => of(ccStudyParticipantActions.getCcStudyParticipantsError({ error })))
        );
      })
    )
  );

  deleteCcStudyParticipant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ccStudyParticipantActions.deleteCcStudyParticipant),
      switchMap(({ studyId, ccStudyParticipantId }) => {
        return this.studyParticipantsService.deleteStudyParticipant(studyId, ccStudyParticipantId).pipe(
          map(() =>
            ccStudyParticipantActions.deleteCcStudyParticipantSuccess({
              deletedCcStudyParticipantId: ccStudyParticipantId,
            })
          ),
          catchError((err: HttpErrorResponse) =>
            of(
              ccStudyParticipantActions.deleteCcStudyParticipantError({
                error: err,
              })
            )
          )
        );
      })
    )
  );

  deleteCcStudyParticipantSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ccStudyParticipantActions.deleteCcStudyParticipantSuccess),
        tap(({ deletedCcStudyParticipantId }) => this.toastrService.success(`Participant deleted successfully`))
      ),
    { dispatch: false }
  );

  deleteCcStudyParticipantError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ccStudyParticipantActions.deleteCcStudyParticipantError),
        tap(() => this.toastrService.error(`Unable to delete participant`))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private studyParticipantsService: StudyParticipantsService,
    private dashboardSnakeCase: DashboardSnakeCasePipe,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private toastrService: ToastrService
  ) {}
}
